// redux/slices/chatPhotoSlice.js (WEB)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

const authHeaders = async () => {
  const token = await getJwtToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/*
 * ✅ Сжатие фото на вебе — аналог expo-image-manipulator:
 * resize до max 1080px по ширине + JPEG quality 0.6 → обычно 100-300KB вместо 5-10MB
 */
const compressImage = (file, maxWidth = 1080, quality = 0.6) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      const scale = img.width > maxWidth ? maxWidth / img.width : 1;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("compressImage: toBlob failed"))),
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("compressImage: image load failed"));
    };

    img.src = url;
  });

/* ========================= THUNKS ========================= */

export const fetchChatPhotos = createAsyncThunk(
  "chatPhotos/fetchChatPhotos",
  async (uid, { rejectWithValue }) => {
    try {
      const serverUrl = getServerUrl();
      const headers = await authHeaders();
      const response = await axios.get(`${serverUrl}/chat-photo/${uid}`, { headers });
      return (response.data || []).map((photo) => `${serverUrl}${photo}`);
    } catch (error) {
      return rejectWithValue(getError(error, "fetchChatPhotos failed"));
    }
  }
);

export const uploadChatPhoto = createAsyncThunk(
  "chatPhotos/uploadChatPhoto",
  /* file — это File из <input type="file"> или drag&drop */
  async ({ uid, messageId, file, onProgress }, { rejectWithValue }) => {
    try {
      const compressed = await compressImage(file);

      const formData = new FormData();
      formData.append("uid", uid);
      formData.append("messageId", messageId);
      formData.append("image", compressed, `photo_${messageId}.jpg`);

      const serverUrl = getServerUrl();
      const headers = await authHeaders();

      const response = await axios.post(`${serverUrl}/chat-photo/upload`, formData, {
        headers,
        onUploadProgress: onProgress
          ? (e) => {
              const percent = Math.round((e.loaded * 100) / (e.total || 1));
              onProgress(percent);
            }
          : undefined,
      });

      return `${serverUrl}${response.data.imageUrl}`;
    } catch (error) {
      return rejectWithValue(getError(error, "uploadChatPhoto failed"));
    }
  }
);

/* ========================= STATE ========================= */

const initialState = {
  photos: [],
  status: "idle",
  error: null,
};

/* ========================= SLICE ========================= */

const chatPhotoSlice = createSlice({
  name: "chatPhotos",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatPhotos.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchChatPhotos.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.photos = action.payload;
      })
      .addCase(fetchChatPhotos.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "fetchChatPhotos failed";
      })

      .addCase(uploadChatPhoto.fulfilled, (state, action) => {
        state.photos.push(action.payload);
      })
      .addCase(uploadChatPhoto.rejected, (state, action) => {
        state.error = action.payload || "uploadChatPhoto failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

export default chatPhotoSlice.reducer;
